import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { api, formatEUR, formatDateFR, formatApiError, toISODate } from "../../lib/api";
import { emitDataChanged } from "../../lib/dataBus";
import { X, Hash, CalendarX } from "lucide-react";
import SegmentedPills from "../../components/ui/SegmentedPills";
import { parseDashboardFilters, syncFiltersToUrl, PAYMENT_LABELS } from "../../lib/copilotFilters";
import TabToolbar from "../../components/pilotage/TabToolbar";
import ViewToggle, { useViewMode } from "../../components/pilotage/ViewToggle";
import EmptyState from "../../components/pilotage/EmptyState";
import { CsvMenu } from "../../components/pilotage/CsvActions";
import { SkeletonCards, SkeletonTableRows } from "../../components/ui/Skeleton";

const STATUS = {
  confirmed: { label: "Confirmée", color: "bg-emerald-100 text-emerald-700" },
  pending: { label: "En attente", color: "bg-amber-100 text-amber-700" },
  in_progress: { label: "En cours", color: "bg-sky-100 text-sky-700" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-700" },
  completed: { label: "Terminée", color: "bg-slate-200 text-slate-700" },
};

const emptyManual = {
  vehicle_id: "",
  client_name: "",
  client_email: "",
  client_phone: "",
  start_date: "",
  end_date: "",
  status: "confirmed",
  price_per_day: "",
};


const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function MovementBadge({ b }) {
  const t = todayISO();
  if (b.status === "cancelled") return null;
  if (b.start_date === t)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-bold" data-testid={`movement-depart-${b.id}`}>↗ Départ aujourd'hui</span>;
  if (b.end_date === t)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-600 text-white text-[10px] font-bold" data-testid={`movement-retour-${b.id}`}>↙ Retour aujourd'hui</span>;
  return null;
}

export default function VendorBookings({ embedded = false }) {
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = parseDashboardFilters(searchParams, { statuses: [...Object.keys(STATUS), "ongoing"] });
  const [filter, setFilter] = useState(urlFilters.status || "all");
  const [search, setSearch] = useState(urlFilters.q || "");
  const [payFilter, setPayFilter] = useState(urlFilters.payment || null);
  const [periodFrom, setPeriodFrom] = useState(urlFilters.from || null);
  const [periodTo, setPeriodTo] = useState(urlFilters.to || null);
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useViewMode("bookings", "list");
  const [form, setForm] = useState(emptyManual);
  const [err, setErr] = useState("");

  // Filtres transmis par le Copilote : appliqués quand ils apparaissent dans l'URL
  useEffect(() => {
    const f = parseDashboardFilters(searchParams, { statuses: [...Object.keys(STATUS), "ongoing"] });
    if (f.status) setFilter(f.status);
    if (f.q !== undefined) setSearch(f.q);
    if (f.payment) setPayFilter(f.payment);
    if (f.from) setPeriodFrom(f.from);
    if (f.to) setPeriodTo(f.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeFilter = (k) => {
    setFilter(k);
    syncFiltersToUrl(searchParams, setSearchParams, { status: k === "all" ? null : k });
  };
  const changeSearch = (v) => {
    setSearch(v);
    syncFiltersToUrl(searchParams, setSearchParams, { q: v || null });
  };
  const clearPayFilter = () => {
    setPayFilter(null);
    syncFiltersToUrl(searchParams, setSearchParams, { payment: null });
  };
  const clearPeriod = () => {
    setPeriodFrom(null);
    setPeriodTo(null);
    syncFiltersToUrl(searchParams, setSearchParams, { from: null, to: null });
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/vendor/bookings"), api.get("/vendor/vehicles")])
      .then(([b, v]) => {
        setBookings(b.data.bookings || []);
        setVehicles(v.data.vehicles || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/vendor/bookings/${id}`, { status });
    load();
    emitDataChanged("bookings");
  };

  const [sourceFilter, setSourceFilter] = useState("all");
  const todayStr = new Date().toISOString().slice(0, 10);
  const isOngoing = (b) => b.status === "in_progress" || (b.status === "confirmed" && b.start_date <= todayStr && b.end_date >= todayStr);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (filter === "ongoing") { if (!isOngoing(b)) return false; }
      else if (filter !== "all" && b.status !== filter) return false;
      if (sourceFilter !== "all" && (b.source || "manual") !== sourceFilter) return false;
      if (payFilter) {
        if (b.status === "cancelled") return false;
        const bal = (b.total || 0) - (b.amount_paid || 0);
        const ended = (b.end_date || "") < todayStr;
        if (payFilter === "unpaid" && !(bal > 0)) return false;
        if (payFilter === "partial" && !((b.amount_paid || 0) > 0 && bal > 0)) return false;
        if (payFilter === "paid" && !(bal <= 0 && (b.total || 0) > 0)) return false;
        if (payFilter === "overdue" && !(bal > 0 && ended)) return false;
      }
      if (periodFrom && (b.end_date || "") < periodFrom) return false;
      if (periodTo && (b.start_date || "") > periodTo) return false;
      const q = search.toLowerCase();
      if (q && !(b.client_name.toLowerCase().includes(q) || b.vehicle_name.toLowerCase().includes(q) || (b.license_plate || "").toLowerCase().includes(q) || (b.reference || "").toLowerCase().includes(q))) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, filter, search, sourceFilter, payFilter, periodFrom, periodTo]);

  const totals = useMemo(() => {
    const t = { all: 0, confirmed: 0, pending: 0, in_progress: 0, completed: 0, cancelled: 0, ongoing: 0 };
    bookings.forEach((b) => { t.all++; t[b.status] = (t[b.status] || 0) + 1; if (isOngoing(b)) t.ongoing++; });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const sources = useMemo(() => [...new Set(bookings.map((b) => b.source || "manual"))], [bookings]);


  const content = (
    <div className={embedded ? "p-4 sm:p-6" : "w-full p-6 sm:p-8"}>
        <TabToolbar
          title="Réservations"
          count={totals.all}
          search={search}
          onSearch={changeSearch}
          searchPlaceholder="Rechercher une réservation..."
          searchTestid="search-bookings"
          filtersActive={(filter !== "all" ? 1 : 0) + (payFilter ? 1 : 0) + ((periodFrom || periodTo) ? 1 : 0)}
          filters={
            <SegmentedPills
              items={[
                { key: "all", label: "Toutes", count: totals.all },
                { key: "confirmed", label: "Confirmées", count: totals.confirmed || 0 },
                { key: "in_progress", label: "En cours", count: totals.in_progress || 0 },
                { key: "pending", label: "En attente", count: totals.pending || 0 },
                { key: "completed", label: "Terminées", count: totals.completed || 0 },
                { key: "cancelled", label: "Annulées", count: totals.cancelled || 0 },
              ]}
              value={filter}
              onChange={changeFilter}
              testid="bookings-filter"
              variant="pills"
            />
          }
          viewToggle={<ViewToggle value={viewMode} onChange={setViewMode} testid="bookings-view-toggle" />}
          addLabel="Réservation"
          onAdd={() => navigate("/vendor/pilotage?tab=bookings&booking=new")}
          addTestid="add-booking-btn"
          permissionModule="bookings"
          extra={
            <CsvMenu exportPath="/vendor/bookings/export" exportFilename="reservations.csv" testid="bookings-csv-menu" />
          }
        />

        {(payFilter || periodFrom || periodTo) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="bookings-active-filters">
            <span className="text-[11px] font-semibold text-slate-500">Filtres appliqués :</span>
            {payFilter && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[11px] font-semibold" data-testid="bookings-filter-payment">
                {PAYMENT_LABELS[payFilter]}
                <button onClick={clearPayFilter} aria-label="Retirer le filtre de paiement" className="hover:text-violet-900" data-testid="bookings-filter-payment-clear">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(periodFrom || periodTo) && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[11px] font-semibold" data-testid="bookings-filter-period">
                {periodFrom ? formatDateFR(periodFrom) : "…"} → {periodTo ? formatDateFR(periodTo) : "…"}
                <button onClick={clearPeriod} aria-label="Retirer le filtre de période" className="hover:text-violet-900" data-testid="bookings-filter-period-clear">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <span className="text-[11px] text-slate-400">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span>
          </div>
        )}

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            viewMode === "cards"
              ? <SkeletonCards count={8} testid="bookings-skeleton-cards" />
              : <SkeletonTableRows rows={7} cols={7} testid="bookings-skeleton-rows" />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={CalendarX}
              title="Aucune réservation"
              description={search || filter !== "all" ? "Aucune réservation ne correspond à vos filtres." : "Créez votre première réservation ou attendez vos premières demandes en ligne."}
              actionLabel="Nouvelle réservation"
              onAction={() => navigate("/vendor/pilotage?tab=bookings&booking=new")}
              testid="bookings-empty"
            />
          ) : viewMode === "cards" ? (
            <div className="p-4 grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" data-testid="bookings-cards">
              {filtered.map((b) => (
                <Link key={b.id} to={`/vendor/pilotage?tab=bookings&booking=${b.id}`} className={`rounded-2xl border bg-white p-4 hover:shadow-md transition-all block ${b.status !== "cancelled" && b.start_date === todayISO() ? "border-violet-400 ring-1 ring-violet-200" : b.status !== "cancelled" && b.end_date === todayISO() ? "border-sky-400 ring-1 ring-sky-200" : "border-slate-200 hover:border-violet-300"}`} data-testid={`booking-card-${b.id}`}>
                  <MovementBadge b={b} />
                  <div className="flex items-center justify-between gap-2 mb-2 mt-1.5 first:mt-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS[b.status]?.color || "bg-slate-100"}`}>{STATUS[b.status]?.label || b.status}</span>
                    <span className="text-sm font-bold text-slate-900">{formatEUR(b.total)}</span>
                  </div>
                  <div className="font-heading text-sm font-semibold text-slate-900 truncate">{b.vehicle_name}</div>
                  <div className="text-xs text-slate-500 truncate">{b.client_name}</div>
                  <div className="mt-2 text-xs text-slate-500">{formatDateFR(b.start_date)} → {formatDateFR(b.end_date)} · {b.days} j</div>
                  {b.license_plate && <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono text-slate-600"><Hash className="h-2.5 w-2.5" /> {b.license_plate}</div>}
                </Link>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Client</th>
                    <th className="text-left px-6 py-3 font-semibold">Véhicule</th>
                    <th className="text-left px-6 py-3 font-semibold">Plaque</th>
                    <th className="text-left px-6 py-3 font-semibold">Dates</th>
                    <th className="text-left px-6 py-3 font-semibold">Montant</th>
                    <th className="text-left px-6 py-3 font-semibold">Statut</th>
                    <th className="text-right px-6 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-50" data-testid={`booking-row-${b.id}`}>
                      <td className="px-6 py-4">
                        <Link to={`/vendor/pilotage?tab=bookings&booking=${b.id}`} className="block">
                          <div className="font-medium text-slate-900">{b.client_name}</div>
                          <div className="text-xs text-slate-500">{b.client_email}</div>
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900 max-w-[200px] truncate">{b.vehicle_name}</div>
                        <div className="text-xs text-slate-500">{b.days} jours</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {b.license_plate ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-xs font-mono text-slate-900">
                            <Hash className="h-3 w-3" /> {b.license_plate}
                          </span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <MovementBadge b={b} />
                        <div className="text-slate-900">{formatDateFR(b.start_date)}</div>
                        <div className="text-xs text-slate-500">→ {formatDateFR(b.end_date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold">{formatEUR(b.total)}</div>
                        <div className="text-xs text-violet-600">+ {formatEUR(b.vendor_revenue)} net</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS[b.status]?.color || "bg-slate-100"}`}>
                          {STATUS[b.status]?.label || b.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <select
                            value={b.status}
                            onChange={(e) => updateStatus(b.id, e.target.value)}
                            className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white"
                            data-testid={`status-select-${b.id}`}
                          >
                            <option value="pending">En attente</option>
                            <option value="confirmed">Confirmée</option>
                            <option value="completed">Terminée</option>
                            <option value="cancelled">Annulée</option>
                          </select>
                          <Link to={`/vendor/pilotage?tab=bookings&booking=${b.id}`} className="text-xs font-medium text-violet-600 hover:underline">Détails</Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Manual booking modal */}
    </div>
  );

  return embedded ? content : <VendorLayout>{content}</VendorLayout>;
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600"
        data-testid={testid}
      />
    </div>
  );
}
